import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    Card,
    CardContent,
} from '@mui/material';
import { format } from 'date-fns';
import AddressForm from '../../components/address-form/AddressForm';

function ConsentFormSignature({ onNext }) {
    const [signature, setSignature] = useState({
        printedName: '',
        sin: '',
        signatureDataUrl: '',
        signedAt: new Date(),
        ipAddress: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: '',
        }
    });
    const [error, setError] = useState(null);
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const drawing = useRef(false);

    useEffect(() => {
        // Get IP address
        fetch('https://api.ipify.org?format=json')
            .then(response => response.json())
            .then(data => setSignature(prev => ({ ...prev, ipAddress: data.ip })))
            .catch(console.error);

        // Setup canvas
        const canvas = canvasRef.current;
        canvas.width = canvas.offsetWidth * 2;
        canvas.height = 150 * 2;
        canvas.style.width = '100%';
        canvas.style.height = '150px';

        const ctx = canvas.getContext('2d');
        ctx.scale(2, 2);
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctxRef.current = ctx;
    }, []);

    const handleChange = (field) => (event) => {
        setSignature(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleAddressChange = (field, value) => {
        setSignature(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
    };

    const startDrawing = (e) => {
        drawing.current = true;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };

    const draw = (e) => {
        if (!drawing.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
    };

    const stopDrawing = () => {
        drawing.current = false;
        ctxRef.current.closePath();
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handleSubmit = () => {
        if (!signature.printedName || !signature.sin) {
            setError('Please provide your printed name and SIN');
            return;
        }

        // Check if signature is drawn
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        const hasSignature = Array.from(imageData).some(x => x !== 0);

        if (!hasSignature) {
            setError('Please provide your signature');
            return;
        }

        const payload = {
            ...signature,
            signatureDataUrl: canvas.toDataURL('image/png'),
        };

        onNext(payload);
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Typography variant="h6" gutterBottom>Digital Signature</Typography>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Printed Name"
                                value={signature.printedName}
                                onChange={handleChange('printedName')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="SIN"
                                value={signature.sin}
                                onChange={handleChange('sin')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>Current Address</Typography>
                            <AddressForm
                                address={signature.address}
                                onChange={handleAddressChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>Drawn Signature</Typography>
                            <Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, p: 1, mb: 1 }}>
                                <canvas
                                    ref={canvasRef}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                    style={{ width: '100%', height: 150, touchAction: 'none' }}
                                />
                                <Button variant="outlined" size="small" onClick={clearSignature} sx={{ mt: 1 }}>
                                    Clear Signature
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                                Date/Time: {format(signature.signedAt, 'MMM dd, yyyy HH:mm:ss')}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                IP Address: {signature.ipAddress || 'Loading...'}
                            </Typography>
                        </Grid>

                        {error && (
                            <Grid item xs={12}>
                                <Typography color="error">{error}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit}
                >
                    Sign Documents
                </Button>
            </Box>
        </Box>
    );
}

export default ConsentFormSignature;